// AgencyShiftTable.jsx

import React from 'react';

const AgencyShiftTable = ({
  shifts = [],
  agencies = [],
  agencyRates = [],
  costCentres = [],
  agencyStaff = [],
  onChange,
  addShift,
  onDelete
}) => {
  const handleChange = (id, field, value) => {
    const updated = shifts.find(s => s.id === id);
    if (!updated) return;

    let updatedRow = { ...updated, [field]: value };

    const start = field === 'start_time' ? value : updated.start_time;
    const end = field === 'end_time' ? value : updated.end_time;
    if (start && end) {
      updatedRow.time = `${start} - ${end}`;
      updatedRow.start_time = start;
      updatedRow.end_time = end;
    }

    if (field === 'role') {
      const match = agencyRates.find(r => r.role === value);
      if (match) updatedRow.hourly_rate = match.pay_rate;
    }

    if (field === 'quantity' && (!value || parseInt(value) < 1)) {
      updatedRow.quantity = 1;
    }

    onChange(id, updatedRow);
  };

  const handleAmend = async (row) => {
    const reason = prompt('Reason for amendment:');
    if (!reason) return;

    try {
      const payload = {
        request_id: row.request_id,
        updated_fields: {
          agency_name: row.agency_name,
          role: row.role,
          date: row.date,
          time: row.time,
          quantity: row.quantity,
          cost_centre_id: row.cost_centre_id,
          notes: row.notes,
          agency_assigned_staff: row.agency_assigned_staff
        },
        amendment_reason: reason,
        modified_by: 1
      };

      await fetch('/solo-mdsh-api/staffing/agency-amend-shift', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      alert('✅ Amendment submitted.');
    } catch (err) {
      console.error('Amendment error:', err);
      alert('❌ Failed to submit amendment.');
    }
  };

  return (
    <div className="bg-gray-800 text-white p-4 rounded shadow">
      <table className="w-full text-sm border border-gray-600">
        <thead>
          <tr className="bg-gray-700">
            <th className="p-2">Agency</th>
            <th>Role</th>
            <th>Date</th>
            <th>Start</th>
            <th>End</th>
            <th>Qty</th>
            <th>Cost Centre *</th>
            <th>Assigned Staff</th>
            <th>Notes</th>
            <th>Hourly Rate</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {shifts.map(row => (
            <tr key={row.id} className="border-b border-gray-600">
              <td>
                <select
                  value={row.agency_name || ''}
                  onChange={e => handleChange(row.id, 'agency_name', e.target.value)}
                  className="bg-gray-900 text-white border border-gray-700 rounded p-1"
                >
                  <option value="">Select</option>
                  {agencies.map(a => (
                    <option key={a.agency_id} value={a.name}>{a.name}</option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  value={row.role || ''}
                  onChange={e => handleChange(row.id, 'role', e.target.value)}
                  className="bg-gray-900 text-white border border-gray-700 rounded p-1"
                >
                  <option value="">Select</option>
                  {agencyRates.map((r, idx) => (
                    <option key={idx} value={r.role}>{r.role}</option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="date"
                  value={row.date || ''}
                  onChange={e => handleChange(row.id, 'date', e.target.value)}
                  className="bg-gray-900 text-white border border-gray-700 rounded p-1"
                />
              </td>
              <td>
                <input
                  type="time"
                  value={row.start_time || ''}
                  onChange={e => handleChange(row.id, 'start_time', e.target.value)}
                  className="bg-gray-900 text-white border border-gray-700 rounded p-1"
                />
              </td>
              <td>
                <input
                  type="time"
                  value={row.end_time || ''}
                  onChange={e => handleChange(row.id, 'end_time', e.target.value)}
                  className="bg-gray-900 text-white border border-gray-700 rounded p-1"
                />
              </td>
              <td>
                <input
                  type="number"
                  value={row.quantity || 1}
                  onChange={e => handleChange(row.id, 'quantity', parseInt(e.target.value))}
                  className="w-16 bg-gray-900 text-white border border-gray-700 rounded p-1"
                  min="1"
                />
              </td>
              <td>
                <select
                  value={row.cost_centre_id || ''}
                  onChange={e => handleChange(row.id, 'cost_centre_id', e.target.value)}
                  required
                  className="bg-gray-900 text-white border border-gray-700 rounded p-1"
                >
                  <option value="">Select</option>
                  {costCentres.map(cc => (
                    <option key={cc.cost_centre_id} value={cc.cost_centre_id}>{cc.name}</option>
                  ))}
                </select>
              </td>
              <td>
                <select
                  value={row.agency_assigned_staff || ''}
                  onChange={e => handleChange(row.id, 'agency_assigned_staff', e.target.value)}
                  className="bg-gray-900 text-white border border-gray-700 rounded p-1"
                >
                  <option value="">Select</option>
                  {agencyStaff.map((s, i) => (
                    <option key={i} value={s.full_name}>{s.full_name}</option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  value={row.notes || ''}
                  onChange={e => handleChange(row.id, 'notes', e.target.value)}
                  className="bg-gray-900 text-white border border-gray-700 rounded p-1"
                />
              </td>
              <td>
                <input
                  type="number"
                  step="0.01"
                  value={row.hourly_rate || ''}
                  disabled
                  readOnly
                  className="w-24 bg-gray-700 text-gray-400 border border-gray-600 rounded p-1 cursor-not-allowed"
                />
              </td>
              <td className="flex flex-col gap-1">
                <button onClick={() => onDelete(row.id)} className="text-red-500 hover:underline">
                  Delete
                </button>
                <button onClick={() => handleAmend(row)} className="text-blue-400 hover:underline">
                  Amend
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button
        onClick={addShift}
        className="mt-3 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded shadow"
      >
        + Add Agency Shift
      </button>
    </div>
  );
};

export default AgencyShiftTable;
